<template>
  <b-modal no-fade v-model="dialog.show" size="xl" id="modal-sync-account"
           :hide-footer="true" centered @close="closeModal"
           no-close-on-backdrop
  >
    <template #modal-title>
      Account Synchronization Review
    </template>
    <div>
      <div v-if="syncData != null">
        <div id="app">
          <!-- Balance Changes -->
          <b-row class="mb-2">
            <b-col md="6">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <div class="text-center">
                  <small class="text-muted d-block">Previous Balance</small>
                  <h4>{{ syncData.previousBalance }} {{$t(`currencies.${accountCurrency}.symbol`)}}</h4>
                </div>
                <span class="arrow">→</span>
                <div class="text-center">
                  <small class="text-muted d-block">New Balance</small>
                  <h4 :class="balanceDiff >= 0 ? 'change-positive' : 'change-negative'">
                    {{ syncData.balance }}  {{$t(`currencies.${accountCurrency}.symbol`)}}
                  </h4>
                </div>
              </div>
              <b-alert show :variant="balanceDiff >= 0 ? 'success' : 'danger'" class="mb-0">
                <strong>Difference:</strong>
                <span class="float-right">{{ balanceDiff >= 0 ? '+' : '' }}{{ balanceDiff }}  {{$t(`currencies.${accountCurrency}.symbol`)}}</span>
              </b-alert>
            </b-col>

            <b-col md="6">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <div class="text-center">
                  <small class="text-muted d-block">Previous Credit</small>
                  <h4>{{ syncData.previousCredit }}  {{$t(`currencies.${accountCurrency}.symbol`)}}</h4>
                </div>
                <span class="arrow">→</span>
                <div class="text-center">
                  <small class="text-muted d-block">New Credit</small>
                  <h4 :class="creditDiff >= 0 ? 'change-positive' : 'change-negative'">
                    {{ syncData.credit }}  {{$t(`currencies.${accountCurrency}.symbol`)}}
                  </h4>
                </div>
              </div>
              <b-alert show :variant="creditDiff >= 0 ? 'success' : 'danger'" class="mb-0">
                <strong>Difference:</strong>
                <span class="float-right">{{ creditDiff >= 0 ? '+' : '' }}{{ creditDiff }}  {{$t(`currencies.${accountCurrency}.symbol`)}}</span>
              </b-alert>
            </b-col>
          </b-row>

          <!-- Monetary Transactions -->
          <div class="mb-1">
            <b-button variant="primary" class="text-left" block @click="transactionsOpen = !transactionsOpen">
              <span>{{ transactionsOpen ? '▼' : '▶' }}</span>
              <span class="mb-0 d-inline-block">Monetary Transactions</span>
              <p class="float-right mb-0">
                <b-badge variant="success" > {{ syncData.monetaryTransactions.totalBalance  | numberFormatter }} {{$t(`currencies.${accountCurrency}.symbol`)}}</b-badge>
                &nbsp;
                <b-badge variant="success" > {{ syncData.monetaryTransactions.totalCredit  | numberFormatter }} {{$t(`currencies.${accountCurrency}.symbol`)}}</b-badge>
              </p>

            </b-button>
            <b-collapse v-model="transactionsOpen">
              <b-card-body>
                <b-row>
                  <b-col md="6">
                    <b-row no-gutters class="d-flex border-bottom pb-1 mb-1" style="width: 100%">
                      <b-col cols="6"><h6>Balance Transactions ({{ Object.keys(syncData.monetaryTransactions.balanceMap).length}})</h6></b-col>
                      <b-col cols="6" class="text-right"><h6>{{ syncData.monetaryTransactions.totalBalance | numberFormatter}} {{$t(`currencies.${accountCurrency}.symbol`)}}</h6></b-col>
                    </b-row>

                    <b-table striped hover small v-handy-scroll :items="balanceTransactions" :fields="transactionFields">
                      <template #cell(transactionId)="data">
                        <code class="small">{{ data.value }}</code>
                      </template>
                      <template #cell(amount)="data">
                        <strong>{{ data.value | numberFormatter}}  {{$t(`currencies.${accountCurrency}.symbol`)}}</strong>
                      </template>
                    </b-table>
                  </b-col>
                  <b-col md="6">
                    <b-row no-gutters class="d-flex border-bottom pb-1 mb-1" style="width: 100%">
                      <b-col cols="6"><h6>Credit Transactions ({{ Object.keys(syncData.monetaryTransactions.creditMap).length }})</h6></b-col>
                      <b-col cols="6" class="text-right"><h6>{{ syncData.monetaryTransactions.totalCredit | numberFormatter}} {{$t(`currencies.${accountCurrency}.symbol`)}}</h6></b-col>
                    </b-row>
                    <b-table
                        striped
                        hover
                        small
                        v-handy-scroll
                        :items="creditTransactions"
                        :fields="transactionFields"
                    >
                      <template #cell(transactionId)="data">
                        <code class="small">{{ data.value }}</code>
                      </template>
                      <template #cell(amount)="data">
                        <strong>{{ data.value | numberFormatter}}  {{$t(`currencies.${accountCurrency}.symbol`)}}</strong>
                      </template>
                    </b-table>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-collapse>
          </div>

          <!-- Closed Positions P&L -->
          <b-button variant="primary" class="text-left" block @click="pnlOpen = !pnlOpen">
            <span>{{ pnlOpen ? '▼' : '▶' }} </span>
            <span class="mb-0 d-inline-block">Closed Positions P&L ({{ Object.keys(syncData.closedPositionsPnl).length }} positions)</span>
            <p class="float-right mb-0"><b-badge :variant="totalPnl >= 0 ? 'success' : 'danger'" > {{ totalPnl  | numberFormatter }} {{$t(`currencies.${accountCurrency}.symbol`)}}</b-badge></p>
          </b-button>
          <b-collapse v-model="pnlOpen">
            <b-card-body>
              <b-row class="mb-1">
                <b-col md="4">
                  <b-card bg-variant="success" text-variant="white">
                    <h4>Total Profit: +{{ totalProfit | numberFormatter}} ({{ profitableCount }})</h4>
                  </b-card>
                </b-col>
                <b-col md="4">
                  <b-card bg-variant="danger" text-variant="white">
                    <h4>Total Loss: {{ totalLoss  | numberFormatter}} ({{ losingCount }})</h4>
                    <small></small>
                  </b-card>
                </b-col>
                <b-col md="4">
                  <b-card :bg-variant="totalPnl >= 0 ? 'info' : 'warning'" text-variant="white">
                    <h4>Net P&L {{ totalPnl >= 0 ? '+' : '' }}{{ totalPnl  | numberFormatter }} ( {{ winRate  | numberFormatter(0) }}%)</h4>
                  </b-card>
                </b-col>
              </b-row>

              <b-table striped hover responsive v-handy-scroll :items="pnlArray" :fields="pnlFields" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc">
                <template #cell(orderId)="data">
                  <b-badge variant="secondary">{{ data.value }}</b-badge>
                </template>
                <template #cell(pnl)="data">
                  <b-badge :variant="data.value >= 0 ? 'success' : 'danger'" >
                    {{ data.value >= 0 ? '+' : '' }}{{ data.value | numberFormatter }}
                  </b-badge>
                </template>
                <template #cell(status)="data">
                  <b-icon :icon="data.value >= 0 ? 'arrow-up-circle-fill' : 'arrow-down-circle-fill'"
                          :variant="data.value >= 0 ? 'success' : 'danger'">
                  </b-icon>
                </template>
              </b-table>
            </b-card-body>
          </b-collapse>

          <!-- Action Buttons -->
          <b-card bg-variant="light" class="text-center">
            <b-button variant="success" size="lg" class="mr-3" v-bind:disabled="busy" @click="trySyncAccount">
              <b-icon icon="check-circle"></b-icon> Approve Synchronization
            </b-button>
          </b-card>

        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import mixinBase from '@/components/mixin/mixinBase'
import mixinNotifications from '@/components/mixin/mixinNotifications'

const initSyncData = function (syncData) {
  let defaultMonetaryTransactions = { 'totalBalance': 0, 'totalCredit': 0, 'accountId': '0', 'balanceMap': {}, 'creditMap': {} }
  let defaultData = { 'previousBalance': 0.0, 'balance': 0.0, 'previousCredit': 0.0, 'credit': 0.0, 'monetaryTransactions': {}, 'closedPositionsPnl': {} }
  let result = { ...defaultData, ...syncData }
  if (syncData.monetaryTransactions == null) {
    result.monetaryTransactions = defaultMonetaryTransactions
  } else {
    result.monetaryTransactions = { ...defaultMonetaryTransactions, ...syncData.monetaryTransactions }
  }
  return result
}

export default {
  name: 'AccountSyncModal',
  props:['accountId', 'accountCurrency'],
  mixins:[mixinBase, mixinNotifications],
  data: () => ({
    dialog: {
      show: false,
    },
    uniqueId: 0,
    busy: false,
    syncData: null,
    //Generated
    balanceOpen: false,
    transactionsOpen: false,
    pnlOpen: false,
    sortBy: 'pnl',
    sortDesc: true,
    transactionFields: [
      { key: 'transactionId', label: 'Transaction ID', sortable: true },
      { key: 'amount', label: 'Amount', sortable: true }
    ],
    pnlFields: [
      { key: 'orderId', label: 'Order ID', sortable: true },
      { key: 'pnl', label: 'Profit/Loss', sortable: true },
      { key: 'status', label: 'Status' }
    ]
  }),
  computed: {
    balanceDiff() {
      if(this.syncData == null){
        return 0;
      }
      return parseFloat((this.syncData.balance - this.syncData.previousBalance).toFixed(2));
    },
    creditDiff() {
      if(this.syncData == null){
        return 0;
      }
      return this.syncData.credit - this.syncData.previousCredit;
    },
    balanceTransactions() {
      if(this.syncData == null || this.syncData?.monetaryTransactions?.balanceMap == null){
        return [];
      }
      return Object.entries(this.syncData.monetaryTransactions.balanceMap).map(([id, amount]) => ({
        transactionId: id,
        amount: amount
      }));
    },
    creditTransactions() {
      if(this.syncData == null || this.syncData?.monetaryTransactions?.creditMap == null){
        return [];
      }
      return Object.entries(this.syncData.monetaryTransactions.creditMap).map(([id, amount]) => ({
        transactionId: id,
        amount: amount
      }));
    },
    pnlArray() {
      if(this.syncData == null || this.syncData.closedPositionsPnl == null){
        return [];
      }
      return Object.entries(this.syncData.closedPositionsPnl).map(([orderId, pnl]) => ({
        orderId: orderId,
        pnl: pnl,
        status: pnl
      }));
    },
    totalPnl() {
      if (this.syncData.closedPositionsPnl == null){
        return 0;
      }
      return parseFloat(Object.values(this.syncData.closedPositionsPnl)
          .reduce((sum, pnl) => sum + pnl, 0).toFixed(2));
    },
    profitableCount() {
      if (this.syncData.closedPositionsPnl == null){
        return 0;
      }
      return Object.values(this.syncData.closedPositionsPnl)
          .filter(pnl => pnl > 0).length;
    },
    losingCount() {
      if (this.syncData.closedPositionsPnl == null){
        return 0;
      }
      return Object.values(this.syncData.closedPositionsPnl)
          .filter(pnl => pnl < 0).length;
    },
    totalProfit() {
      if (this.syncData.closedPositionsPnl == null){
        return 0;
      }
      return parseFloat(Object.values(this.syncData.closedPositionsPnl)
          .filter(pnl => pnl > 0)
          .reduce((sum, pnl) => sum + pnl, 0).toFixed(2));
    },
    totalLoss() {
      if (this.syncData.closedPositionsPnl == null){
        return 0;
      }
      return parseFloat(Object.values(this.syncData.closedPositionsPnl)
          .filter(pnl => pnl < 0)
          .reduce((sum, pnl) => sum + pnl, 0).toFixed(2));
    },
    winRate() {
      if (this.syncData.closedPositionsPnl == null){
        return 0;
      }
      const total = Object.keys(this.syncData.closedPositionsPnl).length;
      return total > 0 ? ((this.profitableCount / total) * 100).toFixed(2) : 0;
    }
  },
  methods:{
    ...mapActions('account', ['syncReportAccount','syncAccount']),
    showModal(){
      this.dialog.show = true;
      this.refresh();
    },
    refresh(){
      if(this.accountId == null){
        return;
      }
      this.syncReportAccount(this.accountId).then((data)=> {
        this.syncData = initSyncData(data);
      });
    },
    trySyncAccount(){
      let $this = this;
      this.busy = true;
      this.syncAccount(this.accountId)
          .then(() => {
            $this.showSuccessNotification('Account sync')
            $this.dialog.show = false;
          }, err=> $this.showServerErrorNotification('updateDataError','account', err))
          .finally(()=>{
            $this.busy = false;
          })
    },
    closeModal(){
      this.syncData = null;
    }
  }
}
</script>

<style scoped>
.change-positive { color: #28a745; font-weight: bold; }
.change-negative { color: #dc3545; font-weight: bold; }
.arrow { font-size: 1.2em; margin: 0 10px; }
.summary-card { background: #f8f9fa; border-left: 4px solid #007bff; }
</style>